import { store } from "@app/store";
import { errorCodeFieldRegex } from "@components/forms/regexp";
import { addAlert } from "@features/alert/slice";
import { logout, refreshAccessToken } from "@features/auth/slice";
import { useCallback, useState } from "react";
import {
  ErrorResponse,
  fetchClient,
  FetchClientError,
  FetchClientOptions,
  JSONFetchClientError,
} from "./client";

export type PaginationResponse<T> = {
  count: number;
  page: number;
} & T;

export type PaginationParameters<T = unknown> = {
  limit: number;
  page: number;
} & T;

type FetchState<T> = {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
};

type UseFetchResult<T> = FetchState<T> & {
  fetch: () => Promise<T | null>;
};

export const useFetch = <T = unknown>(
  url: string,
  method?: FetchClientOptions["method"]
): UseFetchResult<T> => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  return {
    data,
    error,
    isLoading,
    fetch: useCallback(async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchData<T>({ url, method });

        setData(response);

        return response;
      } catch (error) {
        setError(error as Error);

        return null;
      } finally {
        setIsLoading(false);
      }
    }, [method, url]),
  };
};

export const fetchData = async <T = unknown>(
  data: FetchClientOptions
): Promise<T> => {
  const performFetch = async (): Promise<T> => {
    const { accessToken } = store.getState().auth;

    try {
      const result = await fetchClient<T | ErrorResponse>({
        url: data.url,
        headers: {
          ...data.headers,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        method: data.method,
        data: data.data,
      });
      if (
        result &&
        typeof result === "object" &&
        "error" in result &&
        "details" in result
      ) {
        throw new Error(result.details);
      }

      return result;
    } catch (e) {
      if (e instanceof FetchClientError) {
        // if (e.status === 401) {
        //   store.dispatch(logout());
        // }

        const state = store.getState();
        const status = state.auth.status;
        if (e.status === 403 && status == "active") {
          store.dispatch(logout());
        }
      }

      throw e;
    }
  };

  try {
    return await performFetch();
  } catch (error) {
    if (error instanceof Error) {
      if (error instanceof FetchClientError) {
        if (error.status === 401) {
          const { refreshToken } = store.getState().auth;
          if (!refreshToken) {
            store.dispatch(logout());
          }

          try {
            await store.dispatch(refreshAccessToken()).unwrap();
            return await performFetch();
          } catch (err) {
            if (err instanceof FetchClientError) {
              if (
                err.status == 401 &&
                store.getState().auth.status == "active"
              ) {
                store.dispatch(logout());
              }
              throw new Error("Session expired");
            } else {
              store.dispatch(logout());
              throw new Error("Session expired");
            }
          }
        }

        try {
          if (error.data) {
            try {
              const errorData: ErrorResponse = JSON.parse(error.data);

              throw new JSONFetchClientError(errorData);
            } catch (innerError) {
              if (innerError instanceof SyntaxError) {
                throw error;
              }

              throw innerError;
            }
          }
        } catch (requestError) {
          if (requestError instanceof Error) {
            if (
              requestError instanceof JSONFetchClientError &&
              !requestError.data.code?.match(errorCodeFieldRegex)
            ) {
              store.dispatch(addAlert(requestError.message));
              if (requestError?.data?.details) {
                store.dispatch(addAlert(requestError.data.details));
              }
            }

            throw requestError;
          }
        }
      }

      store.dispatch(addAlert(error.message));
    }

    throw error;
  }
};
